import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  User, 
  Mail, 
  Phone,
  Briefcase,
  Euro,
  Calendar,
  AlertCircle,
  CheckCircle,
  RefreshCw
} from 'lucide-react';
import { usePayroll } from '@/hooks/usePayroll';

export const EmployeeList: React.FC = () => {
  const { employees, loading, error, addEmployee, updateEmployee, deleteEmployee, refreshData } = usePayroll();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<string | null>(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    contractType: 'employment' as 'employment' | 'mandate' | 'b2b',
    baseSalary: 0,
    hourlyRate: 0,
    startDate: new Date().toISOString().split('T')[0]
  });

  // Wyczyść komunikaty po 5 sekundach
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitLoading(true);
    setErrorMessage(null);
    
    try {
      if (editingEmployee) {
        const result = await updateEmployee(editingEmployee, formData);
        if (result.error) {
          setErrorMessage(`Błąd aktualizacji: ${result.error}`);
        } else {
          setSuccessMessage('Pracownik został zaktualizowany');
          setEditingEmployee(null);
          setShowAddForm(false);
        }
      } else {
        const result = await addEmployee(formData);
        if (result.error) {
          setErrorMessage(`Błąd dodawania: ${result.error}`);
        } else {
          setSuccessMessage('Pracownik został dodany');
          setShowAddForm(false);
        }
      }
      
      if (!errorMessage) {
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          contractType: 'employment',
          baseSalary: 0,
          hourlyRate: 0,
          startDate: new Date().toISOString().split('T')[0]
        });
      }
    } catch (err) {
      console.error('Submit error:', err);
      setErrorMessage('Wystąpił nieoczekiwany błąd');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (employee: any) => {
    setFormData({
      firstName: employee.firstName,
      lastName: employee.lastName,
      email: employee.email,
      phone: employee.phone,
      contractType: employee.contractType,
      baseSalary: employee.baseSalary,
      hourlyRate: employee.hourlyRate,
      startDate: employee.startDate
    });
    setEditingEmployee(employee.id);
    setShowAddForm(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Czy na pewno chcesz usunąć tego pracownika?')) {
      const result = await deleteEmployee(id);
      if (result.error) {
        setErrorMessage(`Błąd usuwania: ${result.error}`);
      } else {
        setSuccessMessage('Pracownik został usunięty');
      }
    }
  };

  const contractTypeLabels = {
    employment: 'Umowa o pracę',
    mandate: 'Umowa zlecenie',
    b2b: 'B2B'
  };

  return (
    <div className="space-y-6">
      {/* Komunikaty */}
      {successMessage && (
        <div className="bg-green-500/10 border border-green-500 text-green-400 px-4 py-3 rounded-lg flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          {successMessage}
        </div>
      )}
      
      {(error || errorMessage) && (
        <div className="bg-red-500/10 border border-red-500 text-red-400 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {errorMessage || error}
        </div>
      )}

      {/* Nagłówek */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-white">Lista pracowników</h2>
        <div className="flex gap-2">
          <button
            onClick={refreshData}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-700 text-white rounded-lg hover:bg-zinc-600 transition-colors"
            disabled={loading}
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            Odśwież
          </button>
          <button
            onClick={() => {
              setShowAddForm(true);
              setEditingEmployee(null);
              setFormData({
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                contractType: 'employment',
                baseSalary: 0,
                hourlyRate: 0,
                startDate: new Date().toISOString().split('T')[0]
              });
            }}
            className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Dodaj pracownika
          </button>
        </div>
      </div>

      {/* Formularz dodawania/edycji */}
      {showAddForm && (
        <div className="bg-zinc-800 rounded-lg p-6 border border-zinc-700">
          <h3 className="text-lg font-semibold text-white mb-4">
            {editingEmployee ? 'Edytuj pracownika' : 'Nowy pracownik'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Imię
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:border-green-500 focus:outline-none"
                  required
                  disabled={submitLoading}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Nazwisko
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:border-green-500 focus:outline-none"
                  required
                  disabled={submitLoading}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:border-green-500 focus:outline-none"
                  required
                  disabled={submitLoading}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Telefon
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:border-green-500 focus:outline-none"
                  disabled={submitLoading}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Typ umowy
                </label>
                <select
                  value={formData.contractType}
                  onChange={(e) => setFormData({ ...formData, contractType: e.target.value as any })}
                  className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:border-green-500 focus:outline-none"
                  disabled={submitLoading}
                >
                  <option value="employment">Umowa o pracę</option>
                  <option value="mandate">Umowa zlecenie</option>
                  <option value="b2b">B2B</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {formData.contractType === 'b2b' ? 'Stawka miesięczna (netto)' : 'Wynagrodzenie podstawowe (brutto)'}
                </label>
                <input
                  type="number"
                  value={formData.baseSalary}
                  onChange={(e) => setFormData({ ...formData, baseSalary: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:border-green-500 focus:outline-none"
                  step="100"
                  required
                  disabled={submitLoading}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Stawka godzinowa (brutto)
                </label>
                <input
                  type="number"
                  value={formData.hourlyRate}
                  onChange={(e) => setFormData({ ...formData, hourlyRate: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:border-green-500 focus:outline-none"
                  step="0.5"
                  disabled={submitLoading}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Data zatrudnienia
                </label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:border-green-500 focus:outline-none"
                  required
                  disabled={submitLoading}
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setEditingEmployee(null);
                }}
                className="px-4 py-2 bg-zinc-700 text-white rounded-lg hover:bg-zinc-600 transition-colors"
                disabled={submitLoading}
              >
                Anuluj
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={submitLoading}
              >
                {submitLoading ? (
                  <span className="flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Zapisywanie...
                  </span>
                ) : (
                  editingEmployee ? 'Zapisz zmiany' : 'Dodaj pracownika'
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Stan ładowania */}
      {loading && employees.length === 0 && (
        <div className="text-center py-12">
          <RefreshCw className="w-16 h-16 text-gray-600 mx-auto mb-4 animate-spin" />
          <h3 className="text-lg font-semibold text-gray-400 mb-2">Ładowanie pracowników...</h3>
        </div>
      )}

      {/* Lista pracowników */}
      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {employees.map((employee) => (
            <div key={employee.id} className="bg-zinc-800 rounded-lg p-6 border border-zinc-700 hover:border-zinc-600 transition-colors">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-zinc-700 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-gray-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">
                      {employee.firstName} {employee.lastName}
                    </h4>
                    <span className="text-xs text-gray-400">
                      {contractTypeLabels[employee.contractType]}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleEdit(employee)}
                    className="p-2 text-gray-400 hover:text-white hover:bg-zinc-700 rounded transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(employee.id)}
                    className="p-2 text-gray-400 hover:text-red-400 hover:bg-zinc-700 rounded transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-400">
                  <Mail className="w-4 h-4" />
                  <span>{employee.email}</span>
                </div>
                {employee.phone && (
                  <div className="flex items-center gap-2 text-gray-400">
                    <Phone className="w-4 h-4" />
                    <span>{employee.phone}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-gray-400">
                  <Briefcase className="w-4 h-4" />
                  <span>Od: {new Date(employee.startDate).toLocaleDateString('pl-PL')}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <Euro className="w-4 h-4" />
                  <span>
                    {employee.baseSalary.toLocaleString('pl-PL')} zł/mies.
                    {employee.hourlyRate > 0 && ` | ${employee.hourlyRate} zł/h`}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {!loading && employees.length === 0 && !showAddForm && (
        <div className="text-center py-12">
          <User className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-400 mb-2">Brak pracowników</h3>
          <p className="text-gray-500">Dodaj pierwszego pracownika, aby rozpocząć rozliczenia</p>
        </div>
      )}
    </div>
  );
};
